import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { get, getPaginated, post, put, del } from '@/lib/api'

// ==================== Types ====================

export type CashFlowType = 'income' | 'expense'
export type CashFlowRelatedType = 'salary' | 'reimbursement' | 'other'

export interface CashFlow {
  id: string
  accountId: string
  categoryId: string
  type: CashFlowType
  amount: number
  currency: string
  flowDate: string
  description: string | null
  relatedType: CashFlowRelatedType | null
  relatedId: string | null
  createdAt: string
  createdBy: string | null
  updatedAt: string | null
  deletedAt: string | null
}

export interface CashFlowWithRelations extends CashFlow {
  account?: { id: string; name: string } | null
  category?: { id: string; name: string } | null
}

export interface CashFlowStatistics {
  totalIncome: number
  totalExpense: number
  netAmount: number
  byAccount: Array<{ accountId: string; accountName: string; income: number; expense: number }>
  byCategory: Array<{ categoryId: string; categoryName: string; type: CashFlowType; amount: number }>
}

export interface CashFlowListParams {
  accountId?: string
  categoryId?: string
  type?: CashFlowType
  startDate?: string
  endDate?: string
  page?: number
  pageSize?: number
}

export interface CreateCashFlowInput {
  accountId: string
  categoryId: string
  type: CashFlowType
  amount: number
  currency?: string
  flowDate: string
  description?: string
}

export interface UpdateCashFlowInput {
  accountId?: string
  categoryId?: string
  type?: CashFlowType
  amount?: number
  flowDate?: string
  description?: string
}

// ==================== Query Keys ====================

export const cashFlowKeys = {
  all: ['cashFlows'] as const,
  lists: () => [...cashFlowKeys.all, 'list'] as const,
  list: (params: CashFlowListParams) => [...cashFlowKeys.lists(), params] as const,
  detail: (id: string) => [...cashFlowKeys.all, 'detail', id] as const,
  statistics: (params: { startDate?: string; endDate?: string }) =>
    [...cashFlowKeys.all, 'statistics', params] as const,
}

// ==================== Hooks ====================

/**
 * 获取现金流列表
 */
export function useCashFlows(params: CashFlowListParams = {}) {
  return useQuery({
    queryKey: cashFlowKeys.list(params),
    queryFn: () =>
      getPaginated<CashFlowWithRelations>('/finance/cash-flows', params as Record<string, unknown>),
  })
}

/**
 * 获取现金流详情
 */
export function useCashFlow(id: string) {
  return useQuery({
    queryKey: cashFlowKeys.detail(id),
    queryFn: () => get<CashFlowWithRelations>(`/finance/cash-flows/${id}`),
    enabled: !!id,
  })
}

/**
 * 获取现金流统计
 */
export function useCashFlowStatistics(params: { startDate?: string; endDate?: string } = {}) {
  return useQuery({
    queryKey: cashFlowKeys.statistics(params),
    queryFn: () =>
      get<CashFlowStatistics>('/finance/cash-flows/statistics', params as Record<string, unknown>),
  })
}

/**
 * 创建现金流
 */
export function useCreateCashFlow() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateCashFlowInput) => post<CashFlow>('/finance/cash-flows', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: cashFlowKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: cashFlowKeys.statistics({}) })
    },
  })
}

/**
 * 更新现金流
 */
export function useUpdateCashFlow() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCashFlowInput }) =>
      put<CashFlow>(`/finance/cash-flows/${id}`, data),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: cashFlowKeys.detail(id) })
      void queryClient.invalidateQueries({ queryKey: cashFlowKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: cashFlowKeys.statistics({}) })
    },
  })
}

/**
 * 删除现金流
 */
export function useDeleteCashFlow() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => del<null>(`/finance/cash-flows/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: cashFlowKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: cashFlowKeys.statistics({}) })
    },
  })
}
